import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Determines if we're in the holiday season (November 16 to December 31)
 */
export function isHolidaySeason(): boolean {
  const now = new Date();
  const currentYear = now.getFullYear();
  
  // Start date: November 16
  const startDate = new Date(currentYear, 10, 16); // Month is 0-indexed, so 10 = November
  
  // End date: December 31 (end of day)
  const endDate = new Date(currentYear, 11, 31, 23, 59, 59, 999); // 11 = December
  
  return now >= startDate && now <= endDate;
}

interface LightBulbProps {
  color: string;
  isOn: boolean;
  delay: number;
  sequence: number; // 1-4 based on wall clock seconds
}

const LightBulb: React.FC<LightBulbProps> = ({ color, isOn, delay, sequence }) => {
  // Get animation name based on sequence
  // 0 = no animation (all lit), 1-4 = twinkle sequences
  const getAnimationName = () => {
    switch (sequence) {
      case 1: return 'twinkle1';
      case 2: return 'twinkle2';
      case 3: return 'twinkle3';
      case 4: return 'twinkle4';
      case 0: return 'none'; // All lit, no animation
      default: return 'none';
    }
  };

  return (
    <Box
      sx={{
        width: '12px',
        height: '18px',
        // Create a bulb/pear shape: wider at bottom, narrower at top
        // Using an ellipse clip-path positioned to create a bulb shape
        clipPath: 'ellipse(50% 60% at 50% 60%)',
        // Fallback border-radius for bulb shape
        borderRadius: '50% 50% 50% 50% / 35% 35% 65% 65%',
        backgroundColor: isOn ? color : '#2a2a2a',
        boxShadow: isOn
          ? `0 0 12px ${color}, 0 0 24px ${color}, 0 0 36px ${color}, inset 0 0 10px rgba(255,255,255,0.4)`
          : 'inset 0 0 4px rgba(0,0,0,0.5)',
        position: 'relative',
        transition: 'all 0.4s ease',
        animation: isOn && getAnimationName() !== 'none' 
          ? `${getAnimationName()} ${1.2 + delay * 0.3}s ease-in-out infinite` 
          : 'none',
        animationDelay: `${delay}s`,
        border: isOn ? `1px solid ${color}` : '1px solid #444',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-3px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '5px',
          height: '5px',
          borderRadius: '50% 50% 0 0',
          backgroundColor: '#654321', // Brown socket
          border: '1px solid #543210',
          boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
          zIndex: 1,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '45%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isOn ? '5px' : '0px',
          height: isOn ? '5px' : '0px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.6)',
          transition: 'all 0.3s ease',
        },
        // Sequence 1: Gentle pulse (0-15 seconds) - more pronounced
        '@keyframes twinkle1': {
          '0%, 100%': {
            opacity: 1,
            transform: 'scale(1)',
            filter: 'brightness(1.2)',
          },
          '50%': {
            opacity: 0.4,
            transform: 'scale(0.92)',
            filter: 'brightness(0.4)',
          },
        },
        // Sequence 2: Quick flash (15-30 seconds) - more dramatic
        '@keyframes twinkle2': {
          '0%, 100%': {
            opacity: 1,
            transform: 'scale(1)',
            filter: 'brightness(1.2)',
          },
          '25%, 75%': {
            opacity: 0.3,
            transform: 'scale(0.88)',
            filter: 'brightness(0.3)',
          },
          '50%': {
            opacity: 1,
            transform: 'scale(1.08)',
            filter: 'brightness(1.5)',
          },
        },
        // Sequence 3: Slow fade (30-45 seconds) - deeper fade
        '@keyframes twinkle3': {
          '0%, 100%': {
            opacity: 1,
            transform: 'scale(1)',
            filter: 'brightness(1.2)',
          },
          '33%': {
            opacity: 0.35,
            transform: 'scale(0.9)',
            filter: 'brightness(0.35)',
          },
          '66%': {
            opacity: 0.6,
            transform: 'scale(0.94)',
            filter: 'brightness(0.6)',
          },
        },
        // Sequence 4: Double pulse (45-60 seconds) - more extreme
        '@keyframes twinkle4': {
          '0%, 100%': {
            opacity: 1,
            transform: 'scale(1)',
            filter: 'brightness(1.2)',
          },
          '20%, 80%': {
            opacity: 0.4,
            transform: 'scale(0.9)',
            filter: 'brightness(0.4)',
          },
          '40%, 60%': {
            opacity: 0.7,
            transform: 'scale(0.94)',
            filter: 'brightness(0.7)',
          },
          '50%': {
            opacity: 0.25,
            transform: 'scale(0.86)',
            filter: 'brightness(0.25)',
          },
        },
      }}
    />
  );
};

export const ChristmasLights: React.FC = () => {
  const shouldShow = useMemo(() => isHolidaySeason(), []);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentSecond, setCurrentSecond] = useState(0);

  // Measure container width for fixed spacing calculation
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Update current second every second to determine which twinkle sequence to use
  useEffect(() => {
    const updateSecond = () => {
      setCurrentSecond(new Date().getSeconds());
    };
    
    updateSecond(); // Set initial value
    const interval = setInterval(updateSecond, 1000);
    return () => clearInterval(interval);
  }, []);

  // Traditional Christmas light colors - alternating pattern like real strands
  const colors = ['#FF0000', '#00FF00', '#FFFF00', '#0000FF', '#FF00FF', '#00FFFF', '#FFA500'];
  
  // Fixed spacing between lights (increased by 30% to reduce bulb count by 30%)
  const lightSpacing = 39; // pixels (30px * 1.3 = 39px)
  const numSections = 3;
  
  // Calculate how many lights can fit based on container width
  const maxLights = useMemo(() => {
    if (containerWidth === 0) return 0;
    return Math.floor(containerWidth / lightSpacing);
  }, [containerWidth]);
  
  // Calculate lights per section
  const lightsPerSection = Math.floor(maxLights / numSections);
  
  // Determine which sequence to use based on wall clock seconds
  // Sequence 1: 0-5 seconds, Sequence 2: 5-10, Sequence 3: 10-15, Sequence 4: 15-20
  // 0 = all lit (no animation) for 20-60 seconds
  const currentSequence = useMemo(() => {
    if (currentSecond < 5) return 1;
    if (currentSecond < 10) return 2;
    if (currentSecond < 15) return 3;
    if (currentSecond < 20) return 4;
    return 0; // All lit, no animation for 20-60 seconds
  }, [currentSecond]);
  
  // Generate lights with alternating colors - all bulbs on
  // Must be called before any early returns (React Hooks rule)
  const lights = useMemo(() => {
    if (maxLights === 0) return [];
    
    const allLights = [];
    let globalIndex = 0;
    
    for (let section = 0; section < numSections; section++) {
      const sectionStart = section * lightsPerSection;
      const sectionEnd = section === numSections - 1 
        ? maxLights 
        : (section + 1) * lightsPerSection;
      
      for (let i = sectionStart; i < sectionEnd; i++) {
        const sectionSize = sectionEnd - sectionStart;
        const positionInSection = sectionSize > 1 
          ? (i - sectionStart) / (sectionSize - 1) 
          : 0.5; // Center if only one light
        
        allLights.push({
          id: globalIndex++,
          section: section,
          color: colors[globalIndex % colors.length], // Simple alternating pattern
          isOn: true, // All bulbs are on
          delay: (globalIndex * 0.1) % 2,
          positionInSection: positionInSection, // 0 to 1 within the section
          indexInSection: i - sectionStart, // Index within section for pixel positioning
        });
      }
    }
    return allLights;
  }, [maxLights, lightsPerSection]);

  if (!shouldShow) {
    return null;
  }

  // Calculate position on a sagging wire curve
  // Each section follows a quadratic bezier: M 0,0 Q 50,20 100,0
  // For a point at position t (0-1 within the section), we calculate the Y position on the curve
  const getPositionOnWire = (positionInSection: number) => {
    // Quadratic bezier formula: y(t) = (1-t)^2 * y0 + 2*(1-t)*t * y1 + t^2 * y2
    // For our curve: y0=0, y1=20, y2=0
    const y = 2 * (1 - positionInSection) * positionInSection * 20; // Simplified: 40t(1-t)
    return y;
  };
  
  // Calculate the horizontal position in pixels with fixed 30px spacing
  const getHorizontalPosition = (section: number, indexInSection: number) => {
    if (containerWidth === 0) return 0;
    
    const sectionWidth = containerWidth / numSections;
    const sectionStart = section * sectionWidth;
    // Center the lights within each section
    const totalLightsInSection = section === numSections - 1 
      ? maxLights - (numSections - 1) * lightsPerSection
      : lightsPerSection;
    const totalWidthNeeded = (totalLightsInSection - 1) * lightSpacing;
    const sectionOffset = (sectionWidth - totalWidthNeeded) / 2;
    
    return sectionStart + sectionOffset + (indexInSection * lightSpacing);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        height: '70px',
        pointerEvents: 'none',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      {/* Three separate wire sections, each with their own sagging curve */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '70px',
          pointerEvents: 'none',
        }}
        viewBox="0 0 100 70"
        preserveAspectRatio="none"
      >
        {/* Section 1: Left third */}
        <path
          d="M 0,0 Q 16.67,20 33.33,0"
          stroke="#1a1a1a"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="translate(0, 0)"
        />
        {/* Section 2: Middle third */}
        <path
          d="M 0,0 Q 16.67,20 33.33,0"
          stroke="#1a1a1a"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="translate(33.33, 0)"
        />
        {/* Section 3: Right third */}
        <path
          d="M 0,0 Q 16.67,20 33.33,0"
          stroke="#1a1a1a"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="translate(66.67, 0)"
        />
      </svg>
      
      {/* Lights container */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '70px',
        }}
      >
        {lights.map((light) => {
          // Calculate the Y position on the wire curve within this section
          const wireY = getPositionOnWire(light.positionInSection);
          // Calculate the horizontal position in pixels with fixed 30px spacing
          const horizontalPos = getHorizontalPosition(light.section, light.indexInSection);
          
          return (
            <Box
              key={light.id}
              sx={{
                position: 'absolute',
                left: `${horizontalPos}px`,
                top: `${wireY}px`,
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* Wire segment from main wire to light socket */}
              <Box
                sx={{
                  width: '1.5px',
                  height: '6px',
                  backgroundColor: '#1a1a1a',
                  boxShadow: '0 0 1px rgba(0,0,0,0.5)',
                }}
              />
              <LightBulb
                color={light.color}
                isOn={light.isOn}
                delay={light.delay}
                sequence={currentSequence}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

